// Fluxo de telas do totem/balcão: tela atual, tipo de consumo, senha da
// confirmação, inatividade (só no totem) e o "recomeçar". Verbatim do antigo
// BalcaoVendas.js (só virou hook). `onReset` limpa os outros hooks.
import { useState, useEffect, useRef, useCallback } from "react";

export function useTotemFluxo({ autoAtendimento, onReset }) {
  // TOTEM (autoAtendimento): welcome → identificacao (CPF opcional) → menu → cart_review → delivery_type → payment → confirmation
  // BALCÃO (venda rápida): começa direto no menu; nome do cliente opcional; PIX online pede CPF na hora.
  // Desde 29/09/2026 o Balcão pula a cart_review: menu → delivery_type → payment (o carrinho fica sempre à vista).
  const [screen, setScreen] = useState(autoAtendimento ? "welcome" : "menu");
  // Classe da tela: no totem cobre tudo (tela cheia); no balcão fica dentro da área de conteúdo do painel.
  const telaCls = autoAtendimento ? "fixed inset-0 z-50" : "absolute inset-0 z-10";
  const [deliveryType, setDeliveryType] = useState(null); // "local" | "viagem" | "levar_agora"
  const [orderCode, setOrderCode] = useState("");
  const [countdown, setCountdown] = useState(15);
  // Tela inicial do totem: com mídia de marketing no fundo, só o botão fica (pedido do dono, 09/09/2026).
  const [temMidia, setTemMidia] = useState(false);
  const inactivityRef = useRef(null);
  const INACTIVITY_TIMEOUT = 36000; // 36s
  // Quando o timer de inatividade (re)começou — o SAIR desenha o progresso a
  // partir daqui. Ref (não state) pra não re-renderizar a tela a cada toque.
  const inatividadeInicioRef = useRef(Date.now());
  const autoAtendimentoRef = useRef(autoAtendimento);
  useEffect(() => { autoAtendimentoRef.current = autoAtendimento; }, [autoAtendimento]);
  const onResetRef = useRef(onReset);
  useEffect(() => { onResetRef.current = onReset; }, [onReset]);

  // ── Inactivity timer ──
  const resetInactivity = useCallback(() => {
    if (inactivityRef.current) clearTimeout(inactivityRef.current);
    // Só o totem volta sozinho pro início; no balcão o atendente não pode perder o carrinho.
    if (autoAtendimentoRef.current && screen !== "welcome" && screen !== "confirmation") {
      inatividadeInicioRef.current = Date.now();
      inactivityRef.current = setTimeout(() => resetTotem(), INACTIVITY_TIMEOUT);
    }
  }, [screen]);

  useEffect(() => {
    const handler = () => resetInactivity();
    window.addEventListener("pointerdown", handler);
    window.addEventListener("pointermove", handler);
    resetInactivity();
    return () => {
      window.removeEventListener("pointerdown", handler);
      window.removeEventListener("pointermove", handler);
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
    };
  }, [resetInactivity]);

  // ── Auto-reset on confirmation ──
  useEffect(() => {
    if (screen === "confirmation") {
      setCountdown(15);
      const interval = setInterval(() => {
        setCountdown((p) => {
          if (p <= 1) { clearInterval(interval); resetTotem(); return 0; }
          return p - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [screen]);

  const resetTotem = () => {
    setScreen(autoAtendimentoRef.current ? "welcome" : "menu");
    setDeliveryType(null);
    setOrderCode("");
    // Limpa carrinho/checkout e recarrega o cardápio (TURNO: o totem fica ligado o
    // dia inteiro; sem recarregar, atravessaria a virada do turno com o cardápio velho).
    onResetRef.current?.();
  };

  // Trocar de modo (Balcão ⇄ Totem) recomeça do zero; na montagem, carrega o cardápio.
  useEffect(() => {
    resetTotem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAtendimento]);

  return { screen, setScreen, telaCls, deliveryType, setDeliveryType, orderCode, setOrderCode, countdown, temMidia, setTemMidia, resetTotem, autoAtendimentoRef, inatividadeInicioRef, inatividadeMs: INACTIVITY_TIMEOUT };
}
